<script setup lang="ts">
import { inject, ref } from 'vue'
import PageTitle from '@/components/PageTitle.vue'
import { customElementPropsKey } from '@/types/customElementProps.types'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const templateProps = inject(customElementPropsKey)
const formIsValid = ref(false)
const email = ref(templateProps?.email)
const firstname = ref(templateProps?.firstname)
const lastname = ref(templateProps?.lastname)
const password = ref('')
const confirmPassword = ref('')

const requiredRule = (v: string) => !!v || t('error.field.required')
const passwordMatchRule = (v: string) =>
  v === password.value || t('register.error.password-mismatch')

// https://regexr.com/3e48o
const emailRule = (v: string) =>
  /^[\w-\.\+]+@([\w-]+\.)+[\w-]{2,4}$/.test(v) || t('error.email.invalid')
</script>

<template>
  <div>
    <PageTitle
      :title="$t('register.card.title')"
      :subtitle="$t('register.card.subtitle')"
    />
    <v-form
      v-model="formIsValid"
      :action="templateProps?.formActionUrl"
      method="post"
      novalidate
      class="my-6"
    >
      <v-input>
        <v-text-field
          v-model="email"
          type="email"
          autofocus
          name="email"
          autocomplete="email"
          :label="$t('register.email.label')"
          :rules="[requiredRule, emailRule]"
        />
      </v-input>

      <v-input>
        <v-text-field
          v-model="firstname"
          type="text"
          name="firstName"
          autocomplete="firstname"
          :label="$t('register.firstname.label')"
          :rules="[requiredRule]"
        />
      </v-input>

      <v-input>
        <v-text-field
          v-model="lastname"
          type="text"
          name="lastName"
          autocomplete="lastname"
          :label="$t('register.lastname.label')"
          :rules="[requiredRule]"
        />
      </v-input>
      <v-input>
        <v-text-field
          v-model="password"
          type="password"
          name="password"
          autocomplete="new-password"
          :label="$t('register.password.label')"
          :rules="[requiredRule]"
        />
      </v-input>

      <v-input>
        <v-text-field
          v-model="confirmPassword"
          type="password"
          name="password-confirm"
          autocomplete="new-password"
          :label="$t('register.password-confirm.label')"
          :rules="[requiredRule, passwordMatchRule]"
        />
      </v-input>

      <ul class="text-body-2 mx-4 mb-6">
        <li>{{ $t('set-password.requirements.label.first') }}</li>
        <li>{{ $t('set-password.requirements.label.second') }}</li>
        <li>{{ $t('set-password.requirements.label.third') }}</li>
      </ul>

      <v-input>
        <v-btn
          block type="submit"
          :disabled="!formIsValid"
          data-cy="register-submit"
        >
          {{
          $t('register.submit.button')
        }}</v-btn>
      </v-input>
    </v-form>
  </div>
</template>
